import React, { useState } from "react";
import { Grid, GridColumn, GridCustomCellProps, GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import { useTranslation } from "react-i18next";
import { Button, Toolbar } from "@progress/kendo-react-buttons";
import { trashIcon } from "@progress/kendo-svg-icons";
import { useProjectPermission } from "./usePermissionCheck";
import { useDeleteFileDialog, type DeleteTarget } from "./useDeleteFileDialog";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Breadcrumb, BreadcrumbLinkMouseEvent } from "@progress/kendo-react-layout";
import { SELECTED_FIELD } from "@presentation/components/helpers/constants";
import { RegexFilterCell, RegexFilterProvider, useRegexFilter } from "@presentation/components/helpers/RegexFilterBar";
import { ProjectRegexKey, useProjectRegexDefault } from "@presentation/components/helpers/useProjectRegexDefault";
import { ExplorerItem, ExplorerStoreSlice, ShiftSelectionEvent, UseRemoteFolderExplorerOptions, useRemoteFolderExplorer } from "@presentation/components/helpers/useRemoteFolderExplorer";
import { ResetSelectionButton } from "./ResetSelectionButton";
import { ProjectActions } from "@domain/schemas/workspace";

const SelectionCheckbox: React.FC<{
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (isChecked: boolean, event: ShiftSelectionEvent) => void;
}> = ({ checked, indeterminate = false, disabled = false, ariaLabel, onChange }) => (
  <span onClick={(event) => event.stopPropagation()} style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0 }}>
    <Checkbox
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(event) => {
        if (event.syntheticEvent) event.syntheticEvent.stopPropagation();
        const shiftEvent = (event.syntheticEvent ?? event.nativeEvent) as ShiftSelectionEvent;
        onChange(Boolean(event.value), shiftEvent);
      }}
    />
    {indeterminate && (
      <span
        aria-hidden={true}
        style={{ position: "absolute", width: "8px", height: "2px", borderRadius: "999px", backgroundColor: disabled ? "#9ca3af" : "var(--kendo-color-primary, #0058e9)", pointerEvents: "none" }}
      />
    )}
  </span>
);

export interface RemoteFolderExplorerRenderApi {
  /**
  Clears the cache and re-fetches the folder currently being browsed - call after a successful upload.
  */
  refresh: () => Promise<void>;
}

export interface RemoteFolderExplorerGridProps<TApiItem> extends Omit<UseRemoteFolderExplorerOptions<TApiItem>, "store"> {
  store: ExplorerStoreSlice;
  apiRef?: React.Ref<RemoteFolderExplorerRenderApi>;
  /**
  Storage key suffix for the project's regex filter default (see `useProjectRegexDefault`).
  */
  regexFilterStorageKey: ProjectRegexKey;
  fileNameColumnTitle: string;
  /**
  Rendered in the toolbar next to the breadcrumb - typically an "Upload" button.
  */
  toolbarActions?: (api: RemoteFolderExplorerRenderApi) => React.ReactNode;
  /**
  Rendered below the toolbar when non-null - typically an upload dialog, toggled by `toolbarActions`.
  */
  dialog?: (api: RemoteFolderExplorerRenderApi) => React.ReactNode;
  /**
  Additional `GridColumn`s rendered after the shared Selected/fileName columns.
  */
  children?: React.ReactNode;
  /**
  Hides the size column - SoftwareSettings files have no size field, unlike Raw/Fasta files.
  */
  showSizeColumn?: boolean;
  /**
  Hides the Selected checkbox column entirely - for read-only/browse-only embeddings of this grid.
  Defaults to `true`.
  */
  showSelectionColumn?: boolean;
  /**
  Rendered between the toolbar and the Grid - e.g. Raw's DIA-analysis-rerun banner and selection summary.
  */
  aboveGrid?: React.ReactNode;
  /**
  Extra `GridColumn`s rendered after the shared fileLocation column and before the size column - e.g.
  Raw's fileCount column.
  */
  columnsAfterLocation?: React.ReactNode;
  /**
  Overrides the project's saved regex default for this embedding only (e.g. Raw's `pathRegex` prop when
  used as a caller-scoped picker) - falls back to the project default when not supplied.
  */
  regexDefaultOverride?: string;
  deleteFile?: (target: DeleteTarget) => Promise<boolean>;
}

/**
 * Shared Grid/Toolbar/Breadcrumb chrome around `useRemoteFolderExplorer`: the Selected and fileName
 * columns (with the loading spinner, folder icon, and collision badge), regex filtering, and Kendo
 * paging/sorting are all generic across Raw/Fasta/SoftwareSettings-style pickers - only the upload
 * trigger/dialog and any extra columns (location, size, software/version, ...) are facet-specific.
 */
export function RemoteFolderExplorerGrid<TApiItem>({
  apiRef,
  regexFilterStorageKey,
  fileNameColumnTitle,
  toolbarActions,
  dialog,
  children,
  showSizeColumn = true,
  showSelectionColumn = true,
  aboveGrid,
  columnsAfterLocation,
  regexDefaultOverride,
  deleteFile,
  ...explorerOptions
}: RemoteFolderExplorerGridProps<TApiItem>) {
  const { t: loc } = useTranslation();
  const { store } = explorerOptions;
  const { items, metaState, breadcrumbItems, setMetaState } = store;

  const [dataResult, setDataResult] = useState(process(items, metaState));
  const selectionAnchorIdReference = React.useRef<string | null>(null);

  const projectRegex = useProjectRegexDefault(regexFilterStorageKey);
  const regexFilter = useRegexFilter(regexDefaultOverride ?? projectRegex);
  const scopeRegex = regexFilter.regex;
  const matchesRegexFilter = React.useCallback((item: { fileName: string }) => !scopeRegex || scopeRegex.test(item.fileName), [scopeRegex]);

  const {
    loading,
    loadingFolderId,
    getItemSelectionState,
    toggleSelection,
    toggleVisibleSelection,
    resetSelection,
    handleFolderClick,
    handleBreadcrumbClick,
    fetchData,
    clearCache,
    currentFolder,
    currentSourcePath,
  } = useRemoteFolderExplorer<TApiItem>(explorerOptions);

  const renderApi: RemoteFolderExplorerRenderApi = {
    refresh: async () => {
      clearCache();
      await fetchData(currentFolder, currentSourcePath, true);
    },
  };
  const deletePermission = useProjectPermission(explorerOptions.activeProjectId, ProjectActions.DeleteJobArtifacts);
  const deletion = useDeleteFileDialog({
    projectId: explorerOptions.activeProjectId,
    pathType: explorerOptions.pathType,
    deleteFile: async (target) => {
      if (!deleteFile || !deletePermission.allowed) throw new Error("You do not have permission to delete this file.");
      return deleteFile(target);
    },
    onDeleted: async (target) => {
      const prefix = `${target.id.replace(/\/+$/, "")}/`;
      const remaining = Object.fromEntries(
        Object.entries(store.selectedState).filter(
          ([, file]) => (file.sourcePath ?? null) !== (target.sourcePath ?? null) || (file.path !== target.id && !(target.isFolder && file.path.startsWith(prefix))),
        ),
      );
      store.setSelectedState(remaining);
      explorerOptions.onSelectionChange(Object.values(remaining).filter((file) => !file.isFolder));
      await renderApi.refresh();
    },
  });

  React.useImperativeHandle(apiRef, () => renderApi);

  React.useEffect(() => {
    const processedData = process(
      items.filter((item) => matchesRegexFilter(item)),
      metaState,
    );
    const dataWithSelection = processedData.data.map((item) => ({ ...item, [SELECTED_FIELD]: getItemSelectionState(item) === "all" }));
    setDataResult({ ...processedData, data: dataWithSelection });
  }, [getItemSelectionState, matchesRegexFilter, metaState, items]);

  const handleDataStateChange = (event: GridDataStateChangeEvent) => setMetaState(event.dataState);

  const visibleItems = (dataResult.data ?? []) as ExplorerItem[];
  const fullySelectedVisibleItemCount = visibleItems.filter((item) => getItemSelectionState(item) === "all").length;
  const partiallySelectedVisibleItemCount = visibleItems.filter((item) => getItemSelectionState(item) === "partial").length;
  const areAllVisibleItemsSelected = visibleItems.length > 0 && fullySelectedVisibleItemCount === visibleItems.length;
  const areSomeVisibleItemsSelected = fullySelectedVisibleItemCount > 0 || partiallySelectedVisibleItemCount > 0;
  const isSingleSelection = explorerOptions.selectionMode === "single";
  const preventDeselection = explorerOptions.preventDeselection ?? false;

  const collidingFileNames = React.useMemo(() => {
    const countByName = new Map<string, number>();
    for (const item of visibleItems) countByName.set(item.fileName, (countByName.get(item.fileName) ?? 0) + 1);
    return new Set([...countByName].filter(([, count]) => count > 1).map(([name]) => name));
  }, [visibleItems]);

  return (
    <div className="fill-all" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Toolbar className="page-toolbar" style={{ flex: "0 0 auto", minHeight: "50px", display: "flex", alignItems: "center", gap: "10px" }}>
        <Breadcrumb data={breadcrumbItems} onItemSelect={(event: BreadcrumbLinkMouseEvent) => void handleBreadcrumbClick(event.id ?? "home")} size="medium" style={{ flex: 1, minWidth: 0 }} />
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
          {toolbarActions?.(renderApi)}
          {showSelectionColumn && <ResetSelectionButton hasSelection={Object.keys(store.selectedState).length > 0} disabled={preventDeselection} onReset={resetSelection} />}
        </div>
      </Toolbar>

      {dialog?.(renderApi)}

      {aboveGrid}

      <div style={{ flex: "1 1 auto", minHeight: 0 }}>
        <RegexFilterProvider state={regexFilter}>
          <Grid
            style={{ height: "100%" }}
            resizable={true}
            dataItemKey="rowKey"
            selectedField={SELECTED_FIELD}
            filterable={true}
            sortable={true}
            pageable={{ pageSizes: [10, 20, 50, 100, 500], buttonCount: 5 }}
            data={dataResult.data}
            total={dataResult.total}
            skip={metaState.skip}
            take={metaState.take}
            {...metaState}
            onDataStateChange={handleDataStateChange}
          >
            {loading && !loadingFolderId && (
              <div className="k-loading-mask">
                <span className="k-loading-text">Loading...</span>
                <div className="k-loading-image"></div>
                <div className="k-loading-color"></div>
              </div>
            )}
            {showSelectionColumn && (
              <GridColumn
                field={SELECTED_FIELD}
                title="Selected"
                width="50px"
                filterable={false}
                sortable={false}
                headerCell={() => (
                  <span style={{ display: "inline-flex", justifyContent: "center", width: "100%" }}>
                    <SelectionCheckbox
                      checked={areAllVisibleItemsSelected}
                      indeterminate={!areAllVisibleItemsSelected && areSomeVisibleItemsSelected}
                      disabled={isSingleSelection || visibleItems.length === 0 || (preventDeselection && areAllVisibleItemsSelected)}
                      ariaLabel="Select visible files and folders"
                      onChange={(checked) => {
                        selectionAnchorIdReference.current = null;
                        void toggleVisibleSelection(checked, visibleItems);
                      }}
                    />
                  </span>
                )}
                cell={(properties: GridCustomCellProps) => {
                  const dataItem = properties.dataItem as ExplorerItem;
                  const selectionState = getItemSelectionState(dataItem);
                  return (
                    <td style={{ textAlign: "center" }}>
                      <SelectionCheckbox
                        checked={selectionState === "all"}
                        indeterminate={selectionState === "partial"}
                        disabled={(isSingleSelection && dataItem.isFolder) || (preventDeselection && selectionState === "all")}
                        ariaLabel={`Select ${dataItem.isFolder ? "folder" : "file"} ${dataItem.fileName}`}
                        onChange={(checked, event) => {
                          void toggleSelection(dataItem, checked, visibleItems, event);
                          selectionAnchorIdReference.current = dataItem.rowKey;
                        }}
                      />
                    </td>
                  );
                }}
              />
            )}
            <GridColumn
              field="fileName"
              title={fileNameColumnTitle}
              width="500px"
              sortable={true}
              filterCell={RegexFilterCell}
              cell={(properties) => {
                const dataItem = properties.dataItem as ExplorerItem;
                return (
                  <td onClick={() => void handleFolderClick(dataItem)} style={{ cursor: dataItem.isFolder ? "pointer" : "default" }}>
                    {dataItem.isFolder ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }} title="Click to open folder">
                        {loadingFolderId === dataItem.rowKey ? (
                          <span className="k-icon k-i-loading" style={{ color: "#64748b", fontSize: "12px", width: "12px", height: "12px" }}></span>
                        ) : (
                          <span style={{ width: "12px", display: "inline-block" }}>📁</span>
                        )}
                        <span> {dataItem.fileName}</span>
                        {collidingFileNames.has(dataItem.fileName) && dataItem.sourcePath && <span style={{ color: "#64748b", fontSize: "12px" }}>({dataItem.sourcePath})</span>}
                      </div>
                    ) : (
                      <>
                        <span>📄 </span>
                        {dataItem.fileName}
                        {collidingFileNames.has(dataItem.fileName) && dataItem.sourcePath && <span style={{ color: "#64748b", fontSize: "12px" }}> ({dataItem.sourcePath})</span>}
                      </>
                    )}
                  </td>
                );
              }}
            />
            {children}
            {deleteFile && (
              <GridColumn
                title={loc("actions")}
                width="90px"
                filterable={false}
                sortable={false}
                cell={(properties: GridCustomCellProps) => {
                  const item = properties.dataItem as ExplorerItem;
                  return (
                    <td style={{ textAlign: "center" }}>
                      <Button
                        fillMode="flat"
                        themeColor="error"
                        svgIcon={trashIcon}
                        disabled={!item.id || !explorerOptions.activeProjectId || !deletePermission.allowed}
                        title={`Delete ${item.fileName}`}
                        aria-label={`Delete ${item.isFolder ? "folder" : "file"} ${item.fileName}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          const projectId = explorerOptions.activeProjectId;
                          if (!projectId || !deletePermission.allowed) return;
                          const segments = item.id.replace(/\/+$/, "").split("/");
                          void deletion.openDeleteDialog({
                            projectId,
                            id: item.id,
                            fileName: segments.at(-1) ?? "",
                            folder: segments.slice(0, -1).join("/"),
                            isFolder: item.isFolder,
                            sourcePath: item.sourcePath ?? null,
                          });
                        }}
                      />
                    </td>
                  );
                }}
              />
            )}
            <GridColumn field="id" title={loc("fileLocation")} width="250px" cell={(props: GridCustomCellProps) => <td title={props.dataItem.sourcePath ?? undefined}>{props.dataItem.id ?? ""}</td>} />
            {columnsAfterLocation}
            {showSizeColumn && (
              <GridColumn
                field="sizeInGiB"
                title={loc("fileSizeInGiB")}
                width="auto"
                filter="numeric"
                cell={(properties) => <td>{properties.dataItem.isFolder ? "-" : (properties.dataItem.sizeInBytes / (1024 * 1024 * 1024)).toFixed(2)}</td>}
              />
            )}
          </Grid>
        </RegexFilterProvider>
      </div>
      {deleteFile && deletion.deleteFileDialog}
    </div>
  );
}
